import React, { useEffect, useMemo, useRef, useState } from "react";
import { Videocam, Mic, MicOff, VideocamOff } from "@mui/icons-material";
import ConfirmBox from "../components/ConfirmBox";
import SettingDialogueBox from "./joinScreen/SettingDialogueBox";
import MeetingDetailModal from "./joinScreen/MeetingDetailModal";
import { meetingModes } from "../CONSTS";
import { appThemes } from "../MeetingAppContextDef";
import { useTranslation } from "react-i18next";
import { useMediaDevice } from "@videosdk.live/react-sdk";
import useMediaStream from "../utils/useMediaStream";
import "./JoinScreen.css";

// Removed DotsBoxContainer - not needed

export default function JoinMeeting({
  onClick,
  name,
  setName,
  meetingUrl,
  meetingTitle,
  participantCanToggleSelfWebcam,
  participantCanToggleSelfMic,
  micEnabled,
  webcamEnabled,
  setSelectedMic,
  setSelectedWebcam,
  mode,
  appTheme,
  cameraId,
}) {
  const [nameErr, setNameErr] = useState(false);

  const [setting, setSetting] = useState(
    participantCanToggleSelfWebcam === "true"
      ? "video"
      : participantCanToggleSelfMic === "true"
        ? "audio"
        : null
  );

  const [dlgMuted, setDlgMuted] = useState(false);
  const [dlgDevices, setDlgDevices] = useState(false);

  const [settingDialogueOpen, setSettingDialogueOpen] = useState(false);

  const handleClickOpen = () => {
    setSettingDialogueOpen(true);
  };

  const handleClose = (value) => {
    setSettingDialogueOpen(false);
  };

  useEffect(() => {
    return () => {
      _handleTurnOffMic();
      _handleTurnOffWebcam();
    };
  }, []);

  const [{ webcams, mics }, setDevices] = useState({
    devices: [],
    webcams: [],
    mics: [],
  });

  const audioAnalyserIntervalRef = useRef();

  const videoPlayerRef = useRef();
  const popupVideoPlayerRef = useRef();
  const popupAudioPlayerRef = useRef();

  const [videoTrack, setVideoTrack] = useState(null);
  const [audioTrack, setAudioTrack] = useState(null);

  const webcamOn = useMemo(() => !!videoTrack, [videoTrack]);
  const micOn = useMemo(() => !!audioTrack, [audioTrack]);

  const videoTrackRef = useRef();
  const audioTrackRef = useRef();

  const { getCameras, getMicrophones } = useMediaDevice({
    onDeviceChanged,
  });

  const { getAudioTrack, getVideoTrack } = useMediaStream();

  const changeWebcam = async (deviceId) => {
    const currentvideoTrack = videoTrackRef.current;

    if (currentvideoTrack) {
      currentvideoTrack.stop();
    }

    const stream = await getVideoTrack({ webcamId: deviceId });
    const videoTracks = stream.getVideoTracks();

    const videoTrack = videoTracks.length ? videoTracks[0] : null;

    setVideoTrack(videoTrack);
  };
  const changeMic = async (deviceId) => {
    const currentAudioTrack = audioTrackRef.current;
    currentAudioTrack && currentAudioTrack.stop();
    const stream = await getAudioTrack({ micId: deviceId });
    const audioTracks = stream.getAudioTracks();

    const audioTrack = audioTracks.length ? audioTracks[0] : null;
    clearInterval(audioAnalyserIntervalRef.current);

    setAudioTrack(audioTrack);
  };
  const getDefaultMediaTracks = async ({
    mic,
    webcam,
    firstTime,
    cameraId,
  }) => {
    if (mic) {
      const stream = await getAudioTrack();

      const audioTracks = stream?.getAudioTracks();
      const audioTrack = audioTracks?.length ? audioTracks[0] : null;
      setAudioTrack(audioTrack);
      if (firstTime) {
        setSelectedMic({
          id: audioTrack?.getSettings()?.deviceId,
        });
      }
    }

    if (webcam) {
      const stream = await getVideoTrack({
        webcamId: cameraId,
        encoderConfig: "h2160p_w3840p",
      });
      const videoTracks = stream?.getVideoTracks();
      const videoTrack = videoTracks?.length ? videoTracks[0] : null;
      setVideoTrack(videoTrack);
      if (firstTime) {
        setSelectedWebcam({
          id: videoTrack?.getSettings()?.deviceId,
        });
      }
      handleClickOpen();
    }
  };
  async function startMuteListener() {
    const currentAudioTrack = audioTrackRef.current;

    if (currentAudioTrack) {
      if (currentAudioTrack.muted) {
        setDlgMuted(true);
      }

      currentAudioTrack.addEventListener("mute", (ev) => {
        setDlgMuted(true);
      });
    }
  }

  const getCameraDevices = async () => {
    try {
      let webcams = await getCameras();

      setDevices((devices) => {
        return { ...devices, webcams };
      });
    } catch (err) {
      console.log("Error in getting camera devices", err);
    }
  };

  const getAudioDevices = async () => {
    try {
      let mics = await getMicrophones();

      const hasMic = mics.length > 0;
      if (hasMic) {
        startMuteListener();
      }

      setDevices((devices) => {
        return { ...devices, mics };
      });
    } catch (err) {
      console.log("Error in getting audio devices", err);
    }
  };

  function onDeviceChanged() {
    getCameraDevices();
    getAudioDevices();
  }

  const getDevices = async ({ micEnabled, webcamEnabled, cameraId }) => {
    try {
      const webcams = await getCameras();
      const mics = await getMicrophones();

      setDevices({ webcams, mics });

      const hasMic = mics.length > 0;
      const hasWebcam = webcams.length > 0;

      if (hasMic) {
        startMuteListener();
      }

      getDefaultMediaTracks({
        mic: hasMic && micEnabled,
        webcam: hasWebcam && webcamEnabled,
        firstTime: true,
        cameraId: cameraId,
      });
    } catch (err) {
      console.log(err);
    }
  };
  const _handleTurnOffWebcam = () => {
    const videoTrack = videoTrackRef.current;

    if (videoTrack) {
      videoTrack.stop();

      setVideoTrack(null);
    }
  };
  const _handleTurnOnWebcam = () => {
    const videoTrack = videoTrackRef.current;

    if (!videoTrack) {
      getDefaultMediaTracks({ mic: false, webcam: true, cameraId: cameraId });
    }
  };

  const _toggleWebcam = () => {
    const videoTrack = videoTrackRef.current;

    if (videoTrack) {
      _handleTurnOffWebcam();
    } else {
      _handleTurnOnWebcam();
    }
  };
  const _handleTurnOffMic = () => {
    const audioTrack = audioTrackRef.current;

    if (audioTrack) {
      audioTrack.stop();

      setAudioTrack(null);
    }
  };
  const _handleTurnOnMic = () => {
    const audioTrack = audioTrackRef.current;

    if (!audioTrack) {
      getDefaultMediaTracks({ mic: true, webcam: false });
    }
  };
  const _handleToggleMic = () => {
    const audioTrack = audioTrackRef.current;

    if (audioTrack) {
      _handleTurnOffMic();
    } else {
      _handleTurnOnMic();
    }
  };

  useEffect(() => {
    videoTrackRef.current = videoTrack;

    if (videoTrack) {
      const videoSrcObject = new MediaStream([videoTrack]);

      if (videoPlayerRef.current) {
        videoPlayerRef.current.srcObject = videoSrcObject;
        try {
          videoPlayerRef.current.play();
        } catch (err) {
          console.log("error in video play", err);
        }
      }

      setTimeout(() => {
        if (popupVideoPlayerRef.current) {
          popupVideoPlayerRef.current.srcObject = videoSrcObject;
          try {
            popupVideoPlayerRef.current.play();
          } catch (err) {
            console.log("error in video play", err);
          }
        }
      }, 1000);
    } else {
      if (videoPlayerRef.current) {
        videoPlayerRef.current.srcObject = null;
      }
      if (popupVideoPlayerRef.current) {
        popupVideoPlayerRef.current.srcObject = null;
      }
    }
  }, [videoTrack, setting, settingDialogueOpen]);

  useEffect(() => {
    audioTrackRef.current = audioTrack;
    startMuteListener();
  }, [audioTrack]);

  useEffect(() => {
    getDevices({ micEnabled, webcamEnabled, cameraId });
  }, []);

  const { t } = useTranslation();

  return (
    <>
      <div className="join-podcast-container">
        <div className="join-podcast-wrapper">
          <div className="join-podcast-header">
            Join Podcast
          </div>
          <div className="join-podcast-content">
            <div className="video-section">
              <div className="video-container">
                <video
                  autoPlay
                  playsInline
                  muted
                  ref={videoPlayerRef}
                  controls={false}
                  className="video-player"
                />

                <div className="video-overlay">
                  {participantCanToggleSelfWebcam === "false" && !webcamOn ? (
                    <div className="video-overlay-text">
                      {mode === meetingModes.VIEWER
                        ? "You are not permitted to use your microphone and camera."
                        : "You are not allowed to turn on your camera"}
                    </div>
                  ) : !webcamOn ? (
                    <div className="video-overlay-text">
                      The camera is off
                    </div>
                  ) : null}
                </div>

                <div className="video-controls">
                  {participantCanToggleSelfMic === "true" && (
                    <button
                      onClick={() => _handleToggleMic()}
                      className={`control-button ${micOn ? 'active' : 'inactive'}`}
                      title={micOn ? "Turn off mic" : "Turn on mic"}
                    >
                      {micOn ? <Mic /> : <MicOff />}
                    </button>
                  )}

                  {participantCanToggleSelfWebcam === "true" && (
                    <button
                      onClick={() => _toggleWebcam()}
                      className={`control-button ${webcamOn ? 'active' : 'inactive'}`}
                      title={webcamOn ? "Turn off camera" : "Turn on camera"}
                    >
                      {webcamOn ? <Videocam /> : <VideocamOff />}
                    </button>
                  )}
                </div>
              </div>

              {settingDialogueOpen && (
                <SettingDialogueBox
                  open={true}
                  onClose={handleClose}
                  popupVideoPlayerRef={popupVideoPlayerRef}
                  popupAudioPlayerRef={popupAudioPlayerRef}
                  changeWebcam={changeWebcam}
                  changeMic={changeMic}
                  setting={setting}
                  setSetting={setSetting}
                  webcams={webcams}
                  mics={mics}
                  setSelectedMic={setSelectedMic}
                  setSelectedWebcam={setSelectedWebcam}
                  videoTrack={videoTrack}
                  audioTrack={audioTrack}
                  participantCanToggleSelfMic={participantCanToggleSelfMic}
                  participantCanToggleSelfWebcam={participantCanToggleSelfWebcam}
                  appTheme={appTheme}
                />
              )}
            </div>

            <div className="details-section">
              <MeetingDetailModal
                name={name}
                setName={setName}
                nameErr={nameErr}
                meetingTitle={meetingTitle}
                meetingUrl={meetingUrl}
                setNameErr={setNameErr}
                startMeeting={() => {
                  onClick({ name, webcamOn, micOn });
                }}
                appTheme={appTheme}
              />
            </div>
          </div>

          <ConfirmBox
            appTheme={appTheme}
            open={dlgMuted}
            successText="OKAY"
            onSuccess={() => {
              setDlgMuted(false);
            }}
            title="System mic is muted"
            subTitle="You're default microphone is muted, please unmute it or increase audio
            input volume from system settings."
          />

          <ConfirmBox
            appTheme={appTheme}
            open={dlgDevices}
            successText="DISMISS"
            onSuccess={() => {
              setDlgDevices(false);
            }}
            title="Mic or webcam not available"
            subTitle="Please connect a mic and webcam to speak and share your video in the meeting. You can also join without them."
          />
        </div>
      </div>
    </>
  );
}
